import React from 'react';
import { ArrowLeft, Download, Calendar, Activity, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { useMetrics } from '../hooks/useMetrics';
import { useTopStudents } from '../hooks/useTopStudents';
import { useSidebarState } from '../hooks/useSidebarState';

export function InteractionsReport() {
  const { metrics, loading: metricsLoading, refresh: refreshMetrics } = useMetrics();
  const { students: topStudents, loading: topStudentsLoading, refresh: refreshStudents } = useTopStudents(3);
  const { isCollapsed } = useSidebarState();

  const handleRefresh = () => {
    refreshMetrics();
    refreshStudents();
  };

  // Dados simulados para os gráficos
  const lineData = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      name: `${i + 1}`,
      interacoes: Math.floor(Math.random() * 100),
      media: 50
    }));
  }, []);

  const areaData = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      name: `${i + 1}`,
      engajamento: Math.floor(Math.random() * 100),
      baseline: 40
    }));
  }, []);

  const loading = metricsLoading || topStudentsLoading;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className={`mx-auto transition-all duration-300 ${
        isCollapsed 
          ? 'max-w-[1400px] px-4' 
          : 'max-w-[1200px]'
      }`}>
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline"
              size="icon"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Relatório de Interações</h1>
              <p className="text-muted-foreground text-sm">
                Análise detalhada dos pontos de interação dos alunos
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button>
              <Download className="w-4 h-4 mr-2" />
              Exportar relatório
            </Button>
            <Select defaultValue="12">
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Selecione o período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="12">Últimos 12 meses</SelectItem>
                <SelectItem value="6">Últimos 6 meses</SelectItem>
                <SelectItem value="3">Últimos 3 meses</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Métricas Principais */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Activity className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-base">Total de Interações</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : metrics.totalPoints.toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-emerald-400 mt-1">
                +15.2% em relação ao período anterior
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Média por Aluno</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : metrics.averagePoints.toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-emerald-400 mt-1">
                +8.7% em relação ao período anterior
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Alunos Ativos</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : metrics.activeStudents.toLocaleString('pt-BR')}
              </p>
              <p className="text-sm text-emerald-400 mt-1">
                +12.4% em relação ao período anterior
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Taxa de Engajamento</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : `${((metrics.totalPoints / (metrics.activeStudents * 100)) * 100).toFixed(1)}%`}
              </p>
              <p className="text-sm text-emerald-400 mt-1">
                +20.1% em relação ao período anterior
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Gráficos */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Evolução das Interações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2C2D3C" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#9CA3AF"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#9CA3AF"
                      fontSize={12}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1C1D2C',
                        border: '1px solid #2C2D3C',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#9CA3AF' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="interacoes" 
                      stroke="#6366f1" 
                      strokeWidth={2}
                      dot={{ fill: '#6366f1', strokeWidth: 2 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="media" 
                      stroke="#2C2D3C"
                      strokeDasharray="3 3"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Taxa de Engajamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={areaData}>
                    <defs>
                      <linearGradient id="colorEngajamento" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2C2D3C" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#9CA3AF"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#9CA3AF"
                      fontSize={12}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1C1D2C',
                        border: '1px solid #2C2D3C',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#9CA3AF' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="engajamento"
                      stroke="#6366f1"
                      fillOpacity={1}
                      fill="url(#colorEngajamento)"
                    />
                    <Line
                      type="monotone"
                      dataKey="baseline"
                      stroke="#2C2D3C"
                      strokeDasharray="3 3"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detalhamento */}
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Distribuição de Pontos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Pontos de Abertura</span>
                  <span className="text-xl font-bold">
                    {loading ? "..." : metrics.openingPoints.toLocaleString('pt-BR')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Pontos de Clique</span>
                  <span className="text-xl font-bold">
                    {loading ? "..." : metrics.clickPoints.toLocaleString('pt-BR')}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Pontos de Engajamento</span>
                  <span className="text-xl font-bold">
                    {loading ? "..." : metrics.engagementPoints.toLocaleString('pt-BR')}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Alunos Mais Engajados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {loading ? (
                  <div className="animate-pulse space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-12 bg-background-card rounded"></div>
                    ))}
                  </div>
                ) : (
                  topStudents.map((student, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{student.nome}</p>
                        <p className="text-sm text-muted-foreground">{student.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{student.interaction_points} pts</p>
                        <p className="text-sm text-emerald-400">
                          +{Math.floor(Math.random() * 20)}% este mês
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}